import { ApiError } from '@hostesa/api-client';
import { fetchMe, useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import { Button, Input, Label } from '@hostesa/ui';
import { useForm } from '@tanstack/react-form';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { useRuntime } from '../../lib/runtime-context';
import { firstAccessibleHotelPath } from '../../lib/slug-resolution';

/**
 * Login screen — the only unauthenticated data-entry route.
 *
 * On success it loads memberships and redirects to the `next` param (if it is a
 * safe in-app path) or the user's first accessible hotel. Errors branch on the
 * typed `ApiError` code/status (never on message text) and are announced with
 * text, never colour alone.
 */
export function LoginPage() {
  const { t } = useT('platform');
  const { t: tc } = useT('common');
  const auth = useAuth();
  const runtime = useRuntime();
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });
  const [formError, setFormError] = useState<string | null>(null);

  const emailSchema = z.string().min(1, t('login.emailRequired')).email(t('login.emailInvalid'));
  const passwordSchema = z.string().min(1, t('login.passwordRequired'));

  const form = useForm({
    defaultValues: { email: '', password: '' },
    onSubmit: async ({ value }) => {
      setFormError(null);
      try {
        await auth.login({ email: value.email, password: value.password });

        // Load memberships to compute the post-login destination.
        const me = await fetchMe(runtime.apiClient);
        const next = typeof search.next === 'string' ? search.next : undefined;
        const safeNext = next?.startsWith('/') && !next.startsWith('//') ? next : undefined;
        const target = safeNext ?? firstAccessibleHotelPath(me.memberships) ?? '/onboarding';

        await navigate({ to: target });
      } catch (err) {
        if (err instanceof ApiError) {
          // Branch on stable code/status, not the (localised) message.
          if (err.status === 401) {
            setFormError(tc('auth.errors.invalidCredentials'));
          } else if (err.code === 'invalid_response') {
            setFormError(tc('auth.errors.unknown'));
          } else {
            setFormError(tc('auth.errors.networkError'));
          }
        } else {
          setFormError(tc('auth.errors.unknown'));
        }
      }
    },
  });

  return (
    <main className="grid min-h-screen bg-[var(--color-alabaster)] lg:grid-cols-2">
      {/* Brand panel — the editorial "guestbook" side; hidden below lg. */}
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-[var(--color-charcoal)] px-12 py-14 text-[var(--color-alabaster)] lg:flex">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full opacity-25 blur-3xl"
          style={{ background: 'linear-gradient(135deg,#5e6b76,#9a9a93 48%,#c8b89a)' }}
        />
        <div className="relative flex items-center gap-3">
          <img src="/favicon.svg" alt="" className="h-9 w-9" />
          <span className="font-[var(--font-serif)] text-xl tracking-tight">Hostesa</span>
        </div>
        <div className="relative max-w-md">
          <h2 className="font-[var(--font-display)] text-3xl leading-tight text-balance">
            {t('login.brandTagline')}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-[var(--color-warm-stone-soft)]">
            {t('login.brandSupport')}
          </p>
        </div>
        <p className="relative font-[var(--font-mono)] text-xs tracking-wide text-[var(--color-warm-stone)]">
          Hostesa.ai
        </p>
      </aside>

      {/* Form panel. */}
      <div className="flex items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-sm">
          {/* Compact brand lockup on small screens (no brand panel there). Kept outside
              `login-card` so the card's first text is always the (visible) title. */}
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            <img src="/favicon.svg" alt="" className="h-8 w-8" />
            <span className="font-[var(--font-serif)] text-lg text-[var(--color-charcoal)]">
              Hostesa
            </span>
          </div>
          <div data-testid="login-card">
            <h1 className="font-[var(--font-display)] text-2xl text-[var(--color-charcoal)]">
              {t('login.title')}
            </h1>
            <p className="mt-1.5 mb-8 text-sm text-[var(--color-stone-mid)]">
              {t('login.subtitle')}
            </p>
            <form
              data-testid="login-form"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Browser/password-manager autofill sets the input value without firing React's
                // onChange, so the controlled form state can still be empty here. Sync the live
                // DOM values into the form before validating, so autofilled creds aren't rejected.
                const els = e.currentTarget.elements;
                const emailEl = els.namedItem('email');
                const passwordEl = els.namedItem('password');
                if (emailEl instanceof HTMLInputElement) form.setFieldValue('email', emailEl.value);
                if (passwordEl instanceof HTMLInputElement) {
                  form.setFieldValue('password', passwordEl.value);
                }
                void form.handleSubmit();
              }}
              className="flex flex-col gap-4"
            >
              <form.Field
                name="email"
                validators={{ onBlur: emailSchema }}
                // biome-ignore lint/correctness/noChildrenProp: TanStack Form's field render-prop API
                children={(field) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="login-email">{tc('auth.email')}</Label>
                    <Input
                      id="login-email"
                      name="email"
                      data-testid="login-email-input"
                      type="email"
                      autoComplete="email"
                      placeholder={tc('auth.emailPlaceholder')}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={field.state.meta.errors.length > 0}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p
                        data-testid="login-email-error"
                        role="alert"
                        className="text-xs text-[var(--color-alert)]"
                      >
                        {String(field.state.meta.errors[0]?.message ?? field.state.meta.errors[0])}
                      </p>
                    )}
                  </div>
                )}
              />

              <form.Field
                name="password"
                validators={{ onBlur: passwordSchema }}
                // biome-ignore lint/correctness/noChildrenProp: TanStack Form's field render-prop API
                children={(field) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="login-password">{tc('auth.password')}</Label>
                    <Input
                      id="login-password"
                      name="password"
                      data-testid="login-password-input"
                      type="password"
                      autoComplete="current-password"
                      placeholder={tc('auth.passwordPlaceholder')}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={field.state.meta.errors.length > 0}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p
                        data-testid="login-password-error"
                        role="alert"
                        className="text-xs text-[var(--color-alert)]"
                      >
                        {String(field.state.meta.errors[0]?.message ?? field.state.meta.errors[0])}
                      </p>
                    )}
                  </div>
                )}
              />

              {formError && (
                <p
                  data-testid="login-form-error"
                  role="alert"
                  className="rounded-md border border-[var(--color-alert)] bg-[var(--color-canvas)] px-3 py-2 text-sm text-[var(--color-alert)]"
                >
                  {formError}
                </p>
              )}

              <form.Subscribe
                selector={(state) => state.isSubmitting}
                // biome-ignore lint/correctness/noChildrenProp: TanStack Form's Subscribe render-prop API
                children={(isSubmitting) => (
                  <Button
                    type="submit"
                    data-testid="login-submit-btn"
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    {isSubmitting ? t('login.submitting') : tc('auth.signIn')}
                  </Button>
                )}
              />
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
